"use client";

import * as React from "react";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Input } from "./input";

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  emptyMessage?: string;
  noResultsMessage?: string;
  className?: string;
  id?: string;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return isMobile;
}

export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select",
  searchPlaceholder = "Search...",
  disabled = false,
  open: controlledOpen,
  onOpenChange,
  emptyMessage = "No options available",
  noResultsMessage = "No options found",
  className,
  id,
}: SearchableSelectProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const isMobile = useIsMobile();

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setInternalOpen(next);
      }
      onOpenChange?.(next);
      if (!next) {
        setSearch("");
      }
    },
    [isControlled, onOpenChange]
  );

  const filteredOptions = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(term)
    );
  }, [options, search]);

  const selectedLabel =
    options.find((option) => option.value === value)?.label || value;

  const focusSearchInput = React.useCallback(() => {
    requestAnimationFrame(() => {
      searchInputRef.current?.focus({ preventScroll: true });
    });
  }, []);

  React.useEffect(() => {
    if (open) {
      focusSearchInput();
    }
  }, [open, focusSearchInput]);

  React.useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [search, open]);

  const handleSelect = (optionValue: string) => {
    onValueChange(optionValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={!isMobile}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          id={id}
          type="button"
          disabled={disabled}
          className={cn(
            "border-input data-[placeholder]:text-muted-foreground flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-sm shadow-xs outline-none disabled:cursor-not-allowed disabled:opacity-50",
            !value && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate text-left">
            {value ? selectedLabel : placeholder}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        collisionPadding={isMobile ? 8 : 16}
        avoidCollisions={!isMobile}
        sticky={isMobile ? "always" : undefined}
        className={cn(
          "z-[250] w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-1.5rem)] overflow-hidden border border-gray-200 bg-white p-0 shadow-xl",
          isMobile && "max-h-[min(55dvh,360px)]"
        )}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          focusSearchInput();
        }}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div
          className="shrink-0 border-b border-gray-200 bg-white p-2"
          onPointerDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <Input
            ref={searchInputRef}
            data-searchable-select-input
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={(e) => {
              e.target.focus({ preventScroll: true });
            }}
            className="h-9 bg-white text-base sm:text-sm"
          />
        </div>

        <ul
          ref={listRef}
          className={cn(
            "overflow-y-auto overscroll-contain bg-white p-1 touch-pan-y [-webkit-overflow-scrolling:touch]",
            isMobile ? "max-h-[min(38dvh,240px)]" : "max-h-[min(16rem,calc(100dvh-14rem))]"
          )}
          role="listbox"
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {options.length === 0 ? (
            <li className="px-3 py-4 text-center text-sm text-gray-500">
              {emptyMessage}
            </li>
          ) : filteredOptions.length === 0 ? (
            <li className="px-3 py-4 text-center text-sm text-gray-500">
              {noResultsMessage}
            </li>
          ) : (
            filteredOptions.map((option) => (
              <li key={option.value} role="option" aria-selected={value === option.value}>
                <button
                  type="button"
                  className={cn(
                    "flex min-h-[40px] w-full items-center justify-between rounded-sm bg-white px-3 py-2.5 text-left text-sm text-gray-900 hover:bg-gray-100 active:bg-gray-200",
                    value === option.value && "bg-teal-50 font-medium"
                  )}
                  onClick={() => handleSelect(option.value)}
                >
                  <span className="truncate">{option.label}</span>
                  {value === option.value && (
                    <CheckIcon className="size-4 shrink-0 text-teal-600" />
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
